import {
  callResponseSchema,
  openCallResponseSchema,
  saveOutcomeResponseSchema,
  startCallResponseSchema,
  telephonyTokenResponseSchema,
  type CallView,
  type SaveOutcomeBody,
  type SaveOutcomeResponse,
  type StartCallBody,
  type StartCallResponse,
  type TelephonyTokenResponse,
} from "@gioia365/contracts";
import { queryOptions, type QueryClient } from "@tanstack/react-query";
import { ApiRequestError, apiRequest } from "../api/http";
import { conversationKeys } from "../conversations/conversation-keys";
import { leadKeys } from "../leads/lead-queries";
import { commercialViewOf, tabKey, type TabContext } from "../session/tab-context";
import { taskKeys } from "../tasks/task-keys";

// Le richieste della telefonia del browser (piano M4, Task 13): la chiamata che nasce sul server, il token del browser,
// l'abbandono di una chiamata mai partita, l'esito e la chiamata di chi guarda ancora senza esito («Manca l'esito…»). Ogni
// richiesta parte con la scheda della chiamata (`tab`): l'azienda e la persona di quando la chiamata è nata, anche se intanto
// la scheda guarda come un'altra persona.

/** L'esito come lo sceglie la postazione; la chiave di idempotenza la aggiunge la sessione (`call-session.tsx`). */
export type OutcomeInput = Omit<SaveOutcomeBody, "requestId">;

const callPath = (callId: string) => `/api/calls/${encodeURIComponent(callId)}`;
const leadCallsPath = (leadId: string) => `/api/leads/${encodeURIComponent(leadId)}/calls`;

/** `POST /api/leads/:leadId/calls`: il server decide numero chiamato e numero mostrato e crea la chiamata. */
export function requestCallStart(tab: TabContext, leadId: string, body: StartCallBody): Promise<StartCallResponse> {
  return apiRequest("POST", leadCallsPath(leadId), { schema: startCallResponseSchema, body, tab });
}

/** `POST /api/telephony/token`: il permesso di comporre del browser, per l'azienda della scheda. */
export function requestTelephonyToken(tab: TabContext): Promise<TelephonyTokenResponse> {
  return apiRequest("POST", "/api/telephony/token", { schema: telephonyTokenResponseSchema, tab });
}

/** `POST /api/calls/:callId/abandon`: la chiamata mai arrivata al fornitore si chiude, e il Lead si libera. */
export async function requestCallAbandon(tab: TabContext, callId: string): Promise<CallView> {
  return (await apiRequest("POST", `${callPath(callId)}/abandon`, { schema: callResponseSchema, tab })).call;
}

export async function requestCallHangup(tab: TabContext, callId: string, signal: AbortSignal): Promise<CallView> {
  return (await apiRequest("POST", `${callPath(callId)}/hangup-request`, { schema: callResponseSchema, tab, signal })).call;
}

export async function requestCallView(tab: TabContext, callId: string, signal: AbortSignal): Promise<CallView> {
  return (await apiRequest("GET", callPath(callId), { schema: callResponseSchema, tab, signal })).call;
}

/** `POST /api/calls/:callId/outcome`: l'esito in due passi, una volta sola, con la chiave del browser. */
export function requestOutcomeSave(tab: TabContext, callId: string, body: SaveOutcomeBody): Promise<SaveOutcomeResponse> {
  return apiRequest("POST", `${callPath(callId)}/outcome`, { schema: saveOutcomeResponseSchema, body, tab });
}

/** La chiave della chiamata di chi guarda ancora senza esito su un Lead. */
export function openCallKey(tab: TabContext, leadId: string) {
  return ["scheda", tabKey(tab), "calls", "open", leadId] as const;
}

/**
 * `GET /api/leads/:leadId/calls/open`: la chiamata di chi guarda ancora senza esito su quel Lead, per «Manca l'esito…»
 * (questione 161). Con `poll` (la scheda del browser non sta seguendo una chiamata sua) e una chiamata ancora aperta sul
 * server (la pagina ricaricata durante una chiamata: la chiude il fornitore), la scheda la rilegge ogni cinque secondi, così
 * l'esito si chiede appena la chiamata finisce; la chiamata che la scheda segue la dice già la sessione. Un server senza le
 * rotte delle chiamate (la demo locale, senza telefonia) risponde il `404 not_found` di ogni rotta che non c'è: nessuna
 * chiamata aperta, senza errore e senza nuovi tentativi.
 */
export function openCallQuery(tab: TabContext, leadId: string, options: { poll: boolean }) {
  return queryOptions({
    queryKey: openCallKey(tab, leadId),
    queryFn: async ({ signal }): Promise<CallView | null> => {
      try {
        return (await apiRequest("GET", `${leadCallsPath(leadId)}/open`, { schema: openCallResponseSchema, tab, signal })).call;
      } catch (error) {
        if (error instanceof ApiRequestError && error.status === 404 && error.code === "not_found") return null;
        throw error;
      }
    },
    refetchInterval: (query) => {
      const call = query.state.data;
      return options.poll && call && !call.needsOutcome && call.liveState !== "ended" ? 5_000 : false;
    },
  });
}

/**
 * Dopo una chiamata (finita, abbandonata, con l'esito salvato): la scheda del Lead («Chiama» e il riepilogo), la sua
 * Timeline con ogni filtro e ricerca, la chiamata ancora senza esito, le righe degli elenchi (l'esito dell'ultima chiamata) e
 * l'agenda dei Task (la chiamata può aver completato il richiamo del Lead: piano M5, Task 18) e «Conversazioni» si rileggono. Le chiavi sono quelle dei Lead (`leadKeys`), nella vista della scheda in cui la chiamata è nata (una
 * chiamata nasce sempre fuori da «Guarda come…», e cambiare vista toglie comunque tutte le letture della scheda).
 */
export function refreshAfterCall(queryClient: QueryClient, tab: TabContext, leadId: string): void {
  const tabPart = tabKey(tab);
  const view = commercialViewOf(tab);
  void queryClient.invalidateQueries({ queryKey: ["scheda", tabPart, "today"] });
  void queryClient.invalidateQueries({ queryKey: leadKeys.card(tabPart, leadId, view), exact: true });
  void queryClient.invalidateQueries({ queryKey: leadKeys.timelines(tabPart, leadId, view) });
  void queryClient.invalidateQueries({ queryKey: leadKeys.lists(tabPart) });
  void queryClient.invalidateQueries({ queryKey: openCallKey(tab, leadId) });
  void queryClient.invalidateQueries({ queryKey: taskKeys.all(tabPart) });
  // La telefonata è anche una riga di «Conversazioni» (piano M4, Task 19): esito, durata e registrazione cambiano.
  void queryClient.invalidateQueries({ queryKey: conversationKeys.all(tabPart) });
}
